import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useApprovalRequests } from "../../../hooks/useApprovalRequests";
import { useAuth } from "../../../contexts/authContext";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/tabs";
import { formattedDate } from "../../../lib/utils";
import {
  formatEntityType,
  formatChangeType,
  getEntityLink,
} from "../../../lib/approvalLabels";
import { Link, useSearchParams } from "react-router-dom";
import { ApprovalDetailDialog } from "./ApprovalDetailDialog";
import { CheckCircle, XCircle, RefreshCw, ClipboardList } from "lucide-react";
import { ApprovalRequest } from "../../../models/approval";
import { hasPermission } from "../../../lib/permissions";
import { isChangeDiffEntry } from "../../../lib/controlDiffUtils";

function getDisplayName(request: ApprovalRequest): string {
  if (request.entityName) return request.entityName;

  const diff = request.changeDiff?.["name"];
  if (isChangeDiffEntry(diff)) {
    const val = diff.new ?? diff.old;
    if (typeof val === "string" && val.length > 0) return val;
  }

  const proposed = request.proposedSnapshot?.["name"];
  if (typeof proposed === "string" && proposed.length > 0) return proposed;

  const current = request.currentSnapshot?.["name"];
  if (typeof current === "string" && current.length > 0) return current;

  return request.description ?? "—";
}

export const ApprovalsPage = () => {
  const { workspaceMember } = useAuth();
  const [activeTab, setActiveTab] = useState("pending");
  const [searchParams, setSearchParams] = useSearchParams();

  const pendingFilters = useMemo(() => ({ status: "pending" }), []);
  const approvedFilters = useMemo(() => ({ status: "approved" }), []);

  const {
    loading: loadingPending,
    requests: pendingRequests,
    fetchRequests: fetchPendingRequests,
    approveRequest,
    rejectRequest,
  } = useApprovalRequests({
    fetchOnLoad: true,
    filters: pendingFilters,
  });

  const {
    loading: loadingApproved,
    requests: approvedRequests,
    fetchRequests: fetchApprovedRequests,
  } = useApprovalRequests({
    fetchOnLoad: false,
    filters: approvedFilters,
  });

  const approvedFetched = useRef(false);
  useEffect(() => {
    if (activeTab === "approved" && !approvedFetched.current) {
      approvedFetched.current = true;
      fetchApprovedRequests();
    }
  }, [activeTab, fetchApprovedRequests]);

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    const id = searchParams.get("requestId");
    if (id) {
      setSelectedRequestId(id);
      setDetailOpen(true);
    }
  }, [searchParams]);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDetailOpen(open);
      if (!open) {
        setSearchParams((prev) => {
          prev.delete("requestId");
          return prev;
        });
      }
    },
    [setSearchParams],
  );
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(
    null,
  );

  const isAdmin = hasPermission(workspaceMember, "manage_approvals");

  const handleQuickApprove = useCallback(
    async (e: React.MouseEvent, requestId: string) => {
      e.stopPropagation();
      setQuickActionLoading(requestId);
      await approveRequest(requestId);
      setQuickActionLoading(null);
    },
    [approveRequest],
  );

  const handleQuickReject = useCallback(
    async (e: React.MouseEvent, requestId: string) => {
      e.stopPropagation();
      setQuickActionLoading(requestId);
      await rejectRequest(requestId);
      setQuickActionLoading(null);
    },
    [rejectRequest],
  );

  const handleRowClick = useCallback((requestId: string) => {
    setSelectedRequestId(requestId);
    setDetailOpen(true);
  }, []);

  const handleActionComplete = useCallback(() => {
    fetchPendingRequests();
    if (approvedFetched.current) {
      fetchApprovedRequests();
    }
  }, [fetchPendingRequests, fetchApprovedRequests]);

  const handleRefresh = useCallback(() => {
    if (activeTab === "pending") {
      fetchPendingRequests();
    } else {
      fetchApprovedRequests();
    }
  }, [activeTab, fetchPendingRequests, fetchApprovedRequests]);

  const loading = activeTab === "pending" ? loadingPending : loadingApproved;
  const requests = activeTab === "pending" ? pendingRequests : approvedRequests;

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full mx-auto w-full max-w-6xl p-6 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Approvals</h1>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs and each TabsContent stay flex columns so the table wrapper's
          flex-1 gets a bounded height and scrolls; the page root clips. */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="self-start">
          <TabsTrigger value="pending">
            Pending
            {pendingRequests.length > 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${activeTab === "pending" ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-600"}`}
              >
                {pendingRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved
            {approvedRequests.length > 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${activeTab === "approved" ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-600"}`}
              >
                {approvedRequests.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="flex min-h-0 flex-1 flex-col">
          {pendingRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-stone-400">
              <ClipboardList size={48} className="mb-4" />
              <p className="text-lg font-medium">No pending approvals</p>
              <p className="text-sm">
                Approval requests that need your attention will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Requester</TableHead>
                    <TableHead>Created</TableHead>
                    {isAdmin && (
                      <TableHead className="text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer"
                      onClick={() => handleRowClick(request.id)}
                    >
                      <TableCell className="font-medium">
                        {formatEntityType(request.entityType)}
                      </TableCell>
                      <TableCell>
                        {getEntityLink(request.entityType, request.entityId) ? (
                          <Link
                            to={
                              getEntityLink(
                                request.entityType,
                                request.entityId,
                              ) ?? ""
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {getDisplayName(request)}
                          </Link>
                        ) : (
                          (request.description ?? request.entityName ?? "—")
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {formatChangeType(request.changeType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {request.requester?.email ??
                          request.requesterEmail ??
                          "Deleted user"}
                      </TableCell>
                      <TableCell className="text-stone-500">
                        {formattedDate(request.createdAt)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleQuickApprove(e, request.id)}
                              disabled={quickActionLoading === request.id}
                              title="Approve"
                            >
                              <CheckCircle
                                size={16}
                                className="text-green-600"
                              />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleQuickReject(e, request.id)}
                              disabled={quickActionLoading === request.id}
                              title="Reject"
                            >
                              <XCircle size={16} className="text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="flex min-h-0 flex-1 flex-col">
          {approvedRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-stone-400">
              <ClipboardList size={48} className="mb-4" />
              <p className="text-lg font-medium">No approved requests</p>
              <p className="text-sm">Approved requests will appear here.</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Requester</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer"
                      onClick={() => handleRowClick(request.id)}
                    >
                      <TableCell className="font-medium">
                        {formatEntityType(request.entityType)}
                      </TableCell>
                      <TableCell>
                        {getEntityLink(request.entityType, request.entityId) ? (
                          <Link
                            to={
                              getEntityLink(
                                request.entityType,
                                request.entityId,
                              ) ?? ""
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {getDisplayName(request)}
                          </Link>
                        ) : (
                          getDisplayName(request)
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {formatChangeType(request.changeType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {request.requester?.email ??
                          request.requesterEmail ??
                          "Deleted user"}
                      </TableCell>
                      <TableCell className="text-stone-500">
                        {formattedDate(request.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ApprovalDetailDialog
        requestId={selectedRequestId}
        open={detailOpen}
        onOpenChange={handleDialogOpenChange}
        onActionComplete={handleActionComplete}
      />
    </div>
  );
};
